import styled from 'styled-components';
import { colors } from '@config/colors';
import { heightMedia, media, mediaBelow } from '@config/breakpoints';
import { borderRadius, spacing } from '@config/spacing';
import { typography } from '@config/typography';
import { ART } from './wee-poses';

/** How far a card's glass rises when its beat comes round — the mock's lifted card. */
const LIFT = '44px';

/*
 * Stacked, the section is one screen tall and its height barely moves as
 * the cards fold and unfold — so the open card's picture is what has to
 * give on a short screen. It is sized to the screen it has: the small
 * viewport (100svh: the screen with a phone browser's bars showing) less
 * everything else in the section, measured on a phone —
 *   the nav's foot and the section's top padding      ~94px
 *   the eyebrow, the heading (three lines) and its gap ~158px
 *   the open card's own chrome: title, copy, padding   ~85px
 *   the two folded cards and the gaps between the three ~140px
 *   the Play control's room at the foot                 ~56px
 * — turned into a width by the picture's own aspect. A tall phone keeps
 * the picture at its full width; a short one, or an iPhone with Safari's
 * bars showing, gets the picture that fits with nothing cut off by the
 * fold or the Play control. The floor keeps the five options legible on
 * the smallest phones, where the section scrolls a little instead.
 */
const STACK_CHROME = '533px';
const ART_MIN_WIDTH = '200px';
const ART_MAX_WIDTH = '360px';
const ART_ASPECT = (ART.w / ART.h).toFixed(3);
const STACKED_ART_WIDTH = `clamp(${ART_MIN_WIDTH}, calc((100svh - ${STACK_CHROME}) * ${ART_ASPECT}), ${ART_MAX_WIDTH})`;

export const Root = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  /* Room for the page dots on a phone, as the mock leaves it. */
  padding-right: ${spacing.sm};

  ${media.sm} {
    align-items: center;
    text-align: center;
    padding-right: 0;
  }
`;

/* The mock's seafoam eyebrow, one step deeper so it clears WCAG AA as
   small text on the water. */
export const Eyebrow = styled.div`
  font-family: ${typography.fontFamily.small};
  font-size: ${typography.fontSize.xs};
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${colors.brand.exxxtraDark};
  margin-bottom: ${spacing.lg};
`;

export const Heading = styled.h2`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: clamp(2rem, 4.4vw, 3.25rem);
  font-weight: 300;
  line-height: 1.12;
  letter-spacing: -0.02em;
  color: ${colors.neutral.extraDark};
  max-width: 17em;
  margin: 0 0 ${spacing['2xl']};

  /* A phone: a little closer over the stack, and on a short phone one
     size down too, so three lines of it leave the open card its picture. */
  ${mediaBelow.md} {
    margin-bottom: ${spacing.xl};
  }

  ${heightMedia.shortPhone} {
    font-size: 1.75rem;
    margin-bottom: ${spacing.lg};
  }

  ${media.lg} {
    margin-bottom: ${spacing.xl};
  }
`;

export const Cards = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.lg};

  /* A phone's stack is tighter, for the same reason as the heading. */
  ${mediaBelow.md} {
    gap: ${spacing.md};
  }

  ${media.lg} {
    grid-template-columns: repeat(3, 1fr);
    gap: ${spacing.xl};
    /* Cards stand on one line; a lifted card grows up, into the room
       the others keep above them. */
    align-items: end;
  }
`;

/*
 * A card is its glass. `--a` (0 at rest, 1 in its beat) is written by the
 * story renderer: the glass whitens and its shadow deepens. The card clips
 * its picture, so a lantern's pool or the burst of light never spills past
 * the glass.
 *
 * Stacked, a card is a row — its house and its name — until its beat
 * comes round (`data-open`, also the renderer's), when the house folds
 * away and the body unfolds beneath the name: the whole picture, then the
 * words. The unfolding is CSS's own transition, so the card closing and
 * the next one opening cross at the beat's boundary and the stack's height
 * barely moves.
 *
 * Three across, every card shows its picture above its name, and the
 * current one grows taller by LIFT while the others keep that room above
 * them, so the row's height never changes.
 */
export const Card = styled.article`
  --a: 0;
  position: relative;
  isolation: isolate;
  overflow: hidden;
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-areas:
    'mini title'
    'body body';
  align-items: center;
  padding: ${spacing.md} ${spacing.xl} ${spacing.md} ${spacing.lg};
  border-radius: ${borderRadius['2xl']};
  background: rgba(255, 255, 255, calc(0.36 + 0.32 * var(--a)));
  border: 1px solid rgba(255, 255, 255, 0.75);
  box-shadow: 0 18px 44px rgba(30, 74, 66, calc(0.06 + 0.1 * var(--a)));
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  text-align: left;

  ${media.lg} {
    grid-template-columns: 1fr;
    grid-template-areas:
      'art'
      'title'
      'copy';
    justify-items: center;
    align-content: center;
    text-align: center;
    border-radius: ${borderRadius['3xl']};
    padding: ${spacing.xl} ${spacing.xl} ${spacing['3xl']};
    margin-top: calc(${LIFT} * (1 - var(--a)));
    min-height: calc(392px + ${LIFT} * var(--a));
  }
`;

const FOLD = '700ms cubic-bezier(0.65, 0, 0.35, 1)';

/* The folded card's house; it steps aside as the card opens. */
export const CardMini = styled.div`
  grid-area: mini;
  width: 48px;
  margin-right: ${spacing.md};
  overflow: hidden;
  transition:
    width ${FOLD},
    margin ${FOLD},
    opacity 400ms ease;

  svg {
    display: block;
    width: 48px;
    height: auto;
  }

  [data-open='true'] > & {
    width: 0;
    margin-right: 0;
    opacity: 0;
  }

  ${media.lg} {
    display: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const CardTitle = styled.h3`
  grid-area: title;
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: clamp(1.125rem, 2vw, 1.625rem);
  font-weight: 500;
  line-height: 1.2;
  color: ${colors.neutral.extraDark};
  margin: 0;

  ${media.lg} {
    margin: ${spacing.xs} 0 ${spacing.sm};
  }
`;

/* Folded shut until the card's beat; three across it is not a box at
   all, so its picture and words take the card's own rows. */
export const CardBody = styled.div`
  grid-area: body;
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows ${FOLD};

  [data-open='true'] > & {
    grid-template-rows: 1fr;
  }

  ${media.lg} {
    display: contents;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const CardBodyInner = styled.div`
  min-height: 0;
  overflow: hidden;

  ${media.lg} {
    display: contents;
  }
`;

export const CardArt = styled.div`
  width: 100%;
  max-width: ${ART_MAX_WIDTH};
  max-width: ${STACKED_ART_WIDTH};
  margin: ${spacing.md} auto ${spacing.sm};

  svg {
    display: block;
    width: 100%;
    height: auto;
  }

  ${media.lg} {
    grid-area: art;
    max-width: 300px;
    margin: 0;
  }
`;

export const CardCopy = styled.p`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: ${typography.fontSize.sm};
  font-weight: 400;
  line-height: 1.6;
  color: ${colors.highlight.primary};
  margin: 0 0 ${spacing.sm};

  ${media.lg} {
    grid-area: copy;
    font-size: 15px;
    max-width: 28ch;
    margin: 0 auto;
  }
`;
